import React from 'react'
import InfoIcon from '@material-ui/icons/Info'
import { differenceInDays, isBefore, isAfter, isValid, startOfYesterday, addDays, subDays, subYears } from 'date-fns'
import { SecondaryButton, Modal, SelectBox, DatePicker, PrimaryButton, CustomTooltip } from 'components'
import { formatDateString, parseDate } from 'utils/utilities'

export type SelectReportingPeriodProps = {
  options: { value: string; text?: string }[]
  reportingPeriodState: {
    reportingPeriod: string
    startDate?: string
    endDate?: string
  }
  customPeriodOptions?: {
    customReportingMessage?: React.ReactNode
    minSubYears?: number
    maxIntervalDays?: number
  }
  onPeriodChange(reportingPeriod: string): void
  onCustomPeriodChange?(startDate: string, endDate: string): void
}

type CustomPeriodPickerProps = {
  openCustomPeriod: boolean
  setOpenCustomPeriod: React.Dispatch<React.SetStateAction<boolean>>
} & SelectReportingPeriodProps

type CustomDate = {
  start_date: string
  end_date: string
}

type Fields = keyof CustomDate | 'all'

const CustomPeriodPicker = (props: CustomPeriodPickerProps) => {
  const { reportingPeriodState, customPeriodOptions, onCustomPeriodChange, openCustomPeriod, setOpenCustomPeriod } =
    props
  const { startDate = '', endDate = '' } = reportingPeriodState
  const { minSubYears = 2, maxIntervalDays = 365 } = customPeriodOptions || {}

  const INITIAL_CUSTOM_DATE: CustomDate = {
    start_date: startDate,
    end_date: endDate,
  }

  const [customDate, setCustomDate] = React.useState<CustomDate>(INITIAL_CUSTOM_DATE)

  const validateField = (field: Fields) => {
    switch (field) {
      case 'start_date': {
        if (isBefore(parseDate(customDate.start_date), subYears(startOfYesterday(), minSubYears)))
          return `Date cannot be more than ${minSubYears} years ago!`
        if (isAfter(parseDate(customDate.start_date), startOfYesterday())) return 'Date must be in the past!'
        return ''
      }
      case 'end_date': {
        if (isBefore(parseDate(customDate.end_date), subYears(startOfYesterday(), minSubYears)))
          return `Date cannot be more than ${minSubYears} years ago`
        if (isAfter(parseDate(customDate.end_date), startOfYesterday())) return 'Date must be in the past!'
        if (isBefore(parseDate(customDate.end_date), parseDate(customDate.start_date)))
          return 'Date cannot be before Start Date'
        if (differenceInDays(parseDate(customDate.end_date), parseDate(customDate.start_date)) >= maxIntervalDays)
          return `Date cannot be more than ${maxIntervalDays} days apart`

        return ''
      }
      case 'all': {
        if (!isValid(parseDate(customDate.start_date)) || !isValid(parseDate(customDate.end_date)))
          return 'One or more fields are invalid'
        if (validateField('start_date') || validateField('end_date')) return 'One or more fields are invalid'
        return ''
      }
    }
  }

  const handleCustomDateChange = () => {
    onCustomPeriodChange && onCustomPeriodChange(customDate.start_date, customDate.end_date)
    setOpenCustomPeriod(false)
  }

  const handleClose = () => {
    setOpenCustomPeriod(false)
  }

  return (
    <Modal
      title="Select Custom Period"
      subTitle={`Date intervals up to ${maxIntervalDays} Days through last ${minSubYears} Years`}
      open={openCustomPeriod}
      onClose={handleClose}
    >
      <div className="flex flex-row flex-wrap items-start justify-center gap-8 mb-14">
        <div className="w-full sm:w-64">
          <DatePicker
            id="start-date-input"
            placeholder="Select start date"
            label="Start Date *"
            minDate={
              isValid(parseDate(customDate.end_date)) &&
              isAfter(
                subDays(parseDate(customDate.end_date), maxIntervalDays - 1),
                subYears(startOfYesterday(), minSubYears)
              )
                ? subDays(parseDate(customDate.end_date), maxIntervalDays - 1)
                : subYears(startOfYesterday(), minSubYears)
            }
            maxDate={isValid(parseDate(customDate.end_date)) ? customDate.end_date : startOfYesterday()}
            value={customDate.start_date}
            onChange={(date) => {
              setCustomDate((prevState) => ({
                ...prevState,
                start_date: date,
              }))
            }}
            error={!!validateField('start_date')}
            helperText={validateField('start_date')}
          />
        </div>
        <div className="w-full sm:w-64">
          <DatePicker
            id="end-date-input"
            placeholder="Select end date"
            label="End Date *"
            minDate={
              isValid(parseDate(customDate.start_date))
                ? customDate.start_date
                : subYears(startOfYesterday(), minSubYears)
            }
            maxDate={
              isValid(parseDate(customDate.start_date)) &&
              isBefore(addDays(parseDate(customDate.start_date), maxIntervalDays - 1), startOfYesterday())
                ? addDays(parseDate(customDate.start_date), maxIntervalDays - 1)
                : startOfYesterday()
            }
            value={customDate.end_date}
            onChange={(date) => {
              setCustomDate((prevState) => ({
                ...prevState,
                end_date: date,
              }))
            }}
            error={!!validateField('end_date')}
            helperText={validateField('end_date')}
          />
        </div>
      </div>
      <div className="flex justify-center mb-6">
        <PrimaryButton disabled={!!validateField('all')} buttonText="Ok" onClick={handleCustomDateChange} />
      </div>
    </Modal>
  )
}

export const SelectReportingPeriod = (props: SelectReportingPeriodProps) => {
  const { options, reportingPeriodState, customPeriodOptions, onPeriodChange } = props
  const { reportingPeriod, startDate = '', endDate = '' } = reportingPeriodState
  const { customReportingMessage } = customPeriodOptions || {}

  const [openCustomPeriod, setOpenCustomPeriod] = React.useState<boolean>(false)

  const isCustomPeriod = React.useCallback(() => {
    return reportingPeriod.toLowerCase() === 'custom'
  }, [reportingPeriod])

  const isCustomDateSet = React.useCallback(() => {
    return !!startDate && !!endDate
  }, [startDate, endDate])

  const [fieldError, setFieldError] = React.useState('')
  const validateField = (validation: boolean = false) => {
    if (!reportingPeriod) {
      if (fieldError) return true
      else if (validation) setFieldError('triggered')
    }
    if (isCustomPeriod() && !isCustomDateSet()) {
      if (fieldError) return 'Custom date range not set'
      else if (validation) setFieldError('triggered')
    }
    return false
  }

  const getHelperText = () => {
    const value = validateField()
    if (typeof value === 'string') return value
    if (isCustomPeriod() && isCustomDateSet()) {
      return `From ${formatDateString(startDate)} to ${formatDateString(endDate)}`
    }
    return ''
  }

  const handleReportingPeriodChange = (selectedPeriod: string) => {
    onPeriodChange(selectedPeriod)
    if (selectedPeriod.toLowerCase() === 'custom' && !isCustomDateSet()) {
      setOpenCustomPeriod(true)
    }
  }

  return (
    <div className="flex items-end gap-x-2" data-testid={`select-reporting-period`}>
      <div className="w-full sm:w-64">
        <SelectBox
          placeholder="Select reporting period"
          label="Reporting Period *"
          id="reporting-period"
          value={reportingPeriod}
          onChange={(value) => {
            handleReportingPeriodChange(value)
          }}
          options={options.map((item) => ({
            value: item.value,
            text: item.text ?? item.value,
          }))}
          onBlur={() => validateField(true)}
          error={!!validateField()}
          helperText={getHelperText()}
        />
      </div>
      {!!customReportingMessage && (
        <div className="flex mb-4">
          <CustomTooltip
            openingType="click"
            maxWidth="md"
            value={<div className="flex flex-col text-center">{customReportingMessage}</div>}
          >
            <InfoIcon className="cursor-pointer" style={{ color: '#256BB3' }} />
          </CustomTooltip>
        </div>
      )}
      <div className={isCustomPeriod() ? '' : 'hidden'}>
        <SecondaryButton
          buttonText="Edit dates"
          onClick={() => {
            setOpenCustomPeriod(true)
          }}
        />
      </div>
      {openCustomPeriod && (
        <CustomPeriodPicker {...props} openCustomPeriod={openCustomPeriod} setOpenCustomPeriod={setOpenCustomPeriod} />
      )}
    </div>
  )
}
